import dayjs from 'dayjs';
import { req } from 'react-reqq-lite';
import { toastError } from 'react-geek-toast';
import { useState, useEffect, useCallback } from 'react';
import { each, isEmpty, omitBy, forOwn, get, debounce, uniq } from 'lodash';
import Cookies from 'js-cookie';

export const reqLoader = (constant, value, key = 'get') => {
  req.set(`LOADING/${key}/${constant}`, value);
};

export const parseNumber = (str, default_value = false) => {
  const v = parseFloat(`${str}`.replace(/,/g, ''));
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(v)) return typeof default_value !== 'boolean' ? default_value : str;
  return v;
};

export const formatNumber = (v, decimal = 2) => {
  try {
    const n = parseNumber(v);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(n)) return v;
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  } catch (err) {
    return v;
  }
};

export const formatDate = (date, format = 'MM/DD/YYYY', defaultValue = '-') => {
  if (!date) return defaultValue;
  const d = new Date(date);
  if (d.toString() === 'Invalid Date') return defaultValue;
  return dayjs(d).format(format);
};

export const removeEmpty = (obj) => omitBy(obj, (x) => isEmpty(`${x}`));

export const transformIncluded = (x, included) => {
  if (!included || isEmpty(included)) return x;
  const rowIncluded = {};
  forOwn(x.relationships, (v, k) => {
    rowIncluded[k] = Array.isArray(v.data)
      ? v.data.map(
          (z) =>
            included.find(
              (y) => y.type === get(z, 'type') && y.id === get(z, 'id')
            ) || {}
        )
      : included.find(
          (y) => y.type === get(v, 'data.type') && y.id === get(v, 'data.id')
        ) || {};
  });
  const { links, relationships, type, ...rest } = x;
  return { ...rest, included: rowIncluded };
};

export const getInitials = (str) => {
  const arr = (str || 'SAFIFY').split(' ');
  if (arr.length > 1) {
    return `${arr[0][0]}${arr[1][0]}`.toUpperCase();
  }
  return `${arr[0][0]}${arr[0][1]}`.toUpperCase();
};

export const getInitialsThumb = (str) => {
  return `https://via.placeholder.com/64/ffe1b5/e6a13f?text=${getInitials(
    str
  )}`;
};

export const generate = (length = 10) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; i += 1) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};

export const storage = {
  get: (key, defaultValue = false) => {
    try {
      return JSON.parse(sessionStorage.getItem(key));
    } catch (err) {
      return defaultValue;
    }
  },
  set: (key, value) => {
    const newValue = JSON.stringify(value);
    sessionStorage.setItem(key, newValue);
  },
  remove: (key) => {
    sessionStorage.removeItem(key);
  },
};

export const usePersistState = (
  key,
  state,
  config = { keepOnUnmount: true }
) => {
  const [value, setValue] = useState(storage.get(key) || state);
  // eslint-disable-next-line
  const updateState = useCallback(
    debounce((newState) => {
      storage.set(key, newState);
    }, 300),
    []
  );
  useEffect(() => {
    updateState(value);
  }, [value]); // eslint-disable-line
  useEffect(() => {
    return () => {
      if (!config.keepOnUnmount) {
        storage.remove(key);
      }
    };
  }, []); // eslint-disable-line
  return [value, setValue];
};

export const formatMobileNumber = (num) => {
  if (num.substring(0, 2) === '09') {
    return `639${num.substring(2, 11)}`;
  }
  return num;
};

export const payloadMobileNumber = (num) => {
  if (num.substring(0, 3) === '639') {
    return `09${num.substring(3, 12)}`;
  }
  return num;
};

export const formatSort = ({ sort, ...rest }) => {
  const sortKey = {};
  const [by, order] = (sort || '').split(':');
  if (by && order) {
    sortKey[`sort[${by}]`] = order;
  }
  return {
    ...rest,
    ...sortKey,
  };
};

export const downloadArrayToCsv = (data, filename) => {
  try {
    const rows = [];
    const headers = Object.keys(data[0]);
    rows.push(headers);
    each(data, (v) => {
      const rowArr = [];
      each(v, (r) => {
        rowArr.push(`"${r}"`);
      });
      rows.push(rowArr);
    });
    const csvContent = `data:text/csv;charset=utf-8,${rows
      .map((e) => e.join(','))
      .join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link); // Required for FF
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    toastError('Unable to generate download.');
  }
};

export const queryParams = (params) =>
  Object.keys(params)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');

export const downloadGet = ({ url, filename, params, onSuccess }) => {
  try {
    fetch(`${url}?${queryParams(params)}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${Cookies.get('_token')}`,
      }),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const temp_url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = temp_url;
        a.download = filename || 'filename.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        onSuccess();
      })
      .catch((err) => {
        console.log(err); // eslint-disable-line
        toastError('Unable to process request!');
      });
  } catch (err) {
    toastError('Unable to process request!');
  }
};

export const useOnClickOutside = (ref, onClick, byClassName) => {
  useEffect(() => {
    function handleClickOutside(event) {
      const isInside = () => {
        if (!byClassName) return false;
        const elms = document.getElementsByClassName(byClassName);
        let l = 0;
        for (let i = 0; i < elms.length; i += 1) {
          const el = elms[i];
          if (el && el.contains(event.target)) l += 1;
        }
        return l > 0;
      };
      if (ref.current && !ref.current.contains(event.target) && !isInside()) {
        onClick(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, onClick, byClassName]);
};

export const formatCurrency = (str, currency = 'PHP') => {
  const num = parseNumber(str, '-');
  if (typeof num !== 'number') return '-';
  return Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
};

export const getPermissions = () => {
  try {
    const uniqueSet = uniq(JSON.parse(Cookies.get('_permissions')));
    return uniqueSet;
  } catch (err) {
    return [];
  }
};

export const checkPermissions = (validPermissions) => {
  if (validPermissions.length > 0) {
    const newPermissions = getPermissions()
      ? getPermissions().filter((item) => validPermissions.indexOf(item) > -1)
      : [];
    return validPermissions.length === 0 || newPermissions.length > 0;
  }
  return null;
};

export const authRedirect = () => {
  if (
    checkPermissions([
      'dashboard-dashboard-collection-view',
      'dashboard-dashboard-export',
    ]) === true
  )
    return '/dashboard';

  if (checkPermissions(['news-admin-manage', 'news-admin-view']) === true)
    return '/services/news';

  if (
    checkPermissions([
      'government-agency-admin-manage',
      'government-agency-admin-view',
    ]) === true
  )
    return '/services/gov-agency';

  if (checkPermissions(['tourism-admin-manage', 'tourism-admin-view']) === true)
    return '/services/tourism';

  if (
    checkPermissions([
      'merchant-admin-export',
      'merchant-admin-manage',
      'merchant-admin-view',
    ]) === true
  )
    return '/subscribers/profile/details';

  if (
    checkPermissions(['approver-admin-view', 'approver-admin-manage']) === true
  )
    return '/subscribers/approver';

  if (
    checkPermissions([
      'institution-admin-export',
      'institution-admin-manage',
      'institution-admin-view',
    ]) === true
  )
    return '/billers';

  if (
    checkPermissions([
      'user-administrator-manage',
      'user-administrator-view',
    ]) === true
  )
    return '/acl/users';

  if (
    checkPermissions([
      'admin-access-control-list-manage',
      'admin-access-control-list-view',
    ]) === true
  )
    return '/acl/roles';

  if (
    checkPermissions([
      'admin-access-control-list-manage',
      'admin-access-control-list-view',
    ]) === true
  )
    return '/acl/roles';

  if (
    checkPermissions([
      'settings-applications-cms-application-services-listing-view',
      'settings-applications-cms-application-services-manage',
      'settings-applications-cms-contact-us-listing-view',
      'settings-applications-cms-contact-us-manage',
      'settings-applications-cms-promo-list-ad-listing-listing-view',
      'settings-applications-cms-promo-list-ad-listing-manage',
      'settings-applications-cms-privacy-policy-listing-view',
      'settings-applications-cms-privacy-policy-manage',
      'settings-applications-cms-terms-and-conditions-listing-view',
      'settings-applications-cms-terms-and-conditions-manage',
    ]) === true
  )
    return '/settings/app-cms';

  if (
    checkPermissions([
      'audit-trail-admin-export',
      'audit-trail-filtered-view',
      'audit-trail-listing-view',
    ]) === true
  )
    return '/audit';

  return '/dashboard';
};

export const isValidUrl = (str) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // Protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // Domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // Port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // Query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // Fragment locator

  // Check if the string matches the regular expression
  return pattern.test(str);
};

function hexToRgb(h) {
  let hex = h;
  if (hex.startsWith('#')) {
    hex = hex.slice(1);
  }
  // Convert shorthand hex to full form
  if (hex.length === 3) {
    hex = Array.from(hex)
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return [r, g, b];
}

function rgbToHex(rgb) {
  return `#${rgb
    .map((x) => {
      const hex = x.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    })
    .join('')}`;
}

export function blendHexColors(hexColors, percentage) {
  if (percentage < 0 || percentage > 1) {
    throw new Error('Percentage must be between 0 and 1');
  }
  if (hexColors.length < 2) {
    throw new Error('At least two colors must be provided');
  }

  const rgbColors = hexColors.map(hexToRgb);
  const totalColors = hexColors.length;
  const step = 1 / (totalColors - 1);
  let blendedColor = [0, 0, 0];

  rgbColors.forEach((color, index) => {
    let weight = 1 - Math.abs((percentage - index * step) / step);
    weight = Math.max(Math.min(weight, 1), 0);
    blendedColor = blendedColor.map((bc, i) => bc + color[i] * weight);
  });

  blendedColor = blendedColor.map((channel) =>
    Math.min(255, Math.max(0, Math.round(channel)))
  );

  return rgbToHex(blendedColor);
}
